// @flow
import * as React from 'react';
import { isEmpty } from 'lodash';
import { Link, withRouter } from 'react-router-dom';
import type { Match } from 'react-router-dom';
import styled from 'styled-components';
import {
    Grid, Box, Text, Image,
} from 'grommet';
import LabelText from 'esm-web-pro/components/layout/sidePanel/LabelText';
import moment from 'moment';

export type AttributeType = {
    id?: string,
    label: React.Node,
    value?: React.Node,
    link?: string,
    customLink?: string,
    icon?: string, /* the imported icon */
    multiValue?: Array<{
        value: React.Node,
        link?: string,
    }>,
    dateValue?: string,
    dateFormat?: string, // Optionally, pass a custom date format (default: L)
    AttributeComponent?: React.ComponentType,
    separator?: string,
    onClick?: Function,
    key?: string,
}

type Props = {
    attributes: AttributeType[],
    count: number,
    match: Match,
}

const StyledIcon = styled(Image)`
    max-width: 20px;
    max-height: 20px;
    margin-right: .3em;
`;

const StyledBox = styled(Box)`
    color: blue;
    cursor: pointer;
`;

const NoUnderlineLink = styled(Link)`
    text-decoration: none;
`;

const StyledText = styled(Text)`
    font-family: 'Open Sans';
`;

export const LABEL_COLOR = '#83858e';
export const VALUE_COLOR = '#36363A';

const Attribute = (props: { value: React.Node, link: string, customLink: string,
    onClick?: Function, match: Match, color: string, id?: string }) => {
    const {
        value, link, customLink, onClick, match, color, id,
    } = props;
    if (link) {
        return (
            <NoUnderlineLink to={{
                pathname: link,
                state: {
                    from: match.url,
                },
            }}
            >
                <Box alignContent="center">
                    <StyledText
                        id={id ? `value-${id}` : null}
                        weight={600}
                        color="#0080B4"
                        size="12px"
                    >
                        {value}
                    </StyledText>
                </Box>
            </NoUnderlineLink>
        );
    } if (customLink) {
        return (
            <a href={customLink} style={{ textDecoration: 'none' }}>
                <Box alignContent="center">
                    <StyledText
                        id={id ? `value-${id}` : null}
                        weight={600}
                        color="#0080B4"
                        size="12px"
                    >
                        {value}
                    </StyledText>
                </Box>
            </a>
        );
    }

    if (onClick) {
        return (
            <Box onClick={onClick}>
                <StyledBox>
                    <StyledText
                        id={id ? `value-${id}` : null}
                        weight={600}
                        color="#0080B4"
                        size="12px"
                    >
                        {value}
                    </StyledText>
                </StyledBox>
            </Box>
        );
    }
    return (
        <Box>
            <StyledText
                id={id ? `value-${id}` : null}
                weight={600}
                color={color}
                size="12px"
            >
                {value}
            </StyledText>
        </Box>
    );
};

Attribute.defaultProps = {
    id: '',
    onClick: undefined,
    color: VALUE_COLOR,
};

const Attributes = (props: Props) => {
    const { attributes, count, match } = props;

    return (
        <Box height={{ min: 'auto' }} pad={{ left: 'small', vertical: '15px' }}>
            <Grid
                rows={['auto']}
                columns={{
                    count,
                    size: 'auto',
                }}
            >
                {attributes.map((attribute: AttributeType) => {
                    const {
                        label, link, customLink, icon, multiValue, separator, onClick, color, key: attributeKey,
                        dateValue, dateFormat, id, AttributeComponent,
                    } = attribute;
                    let { value } = attribute;
                    if (isEmpty(value) && isEmpty(multiValue) && isEmpty(dateValue) && typeof value !== 'number') {
                        return null;
                    }
                    const key = attributeKey || (typeof label === 'string' ? label : label.props.id);
                    if (typeof value === 'object' && value && value.code) value = value.name;
                    if (dateValue && !value) value = moment(dateValue).format(dateFormat || 'L');

                    return (
                        <Box
                            id={id || null}
                            align="center"
                            key={key}
                            margin={{ top: '4px' }}
                            direction="row"
                            flex={{ shrink: 0 }}
                        >
                            {icon && <StyledIcon src={icon} />}
                            <Box>
                                <StyledText
                                    id={id ? `label-${id}` : null}
                                    size="9px"
                                    color={LABEL_COLOR}
                                    wordBreak="break-word"
                                >
                                    {label}
                                </StyledText>
                                {/* eslint-disable-next-line no-nested-ternary */}
                                {multiValue ? (
                                    <Box direction="row" wrap>
                                        {
                                            multiValue.map((multiValueAttribute, i) => (
                                                <React.Fragment key={multiValueAttribute.value}>
                                                    {AttributeComponent ? (
                                                        <AttributeComponent
                                                            id={id}
                                                            value={multiValueAttribute.value}
                                                            link={multiValueAttribute.link}
                                                            match={match}
                                                        />
                                                    ) : (
                                                        <Attribute
                                                            id={id}
                                                            value={multiValueAttribute.value}
                                                            link={multiValueAttribute.link}
                                                            match={match}
                                                        />
                                                    )}
                                                    <LabelText style={{ whiteSpace: 'pre' }}>
                                                        {i < multiValue.length - 1 && separator}
                                                    </LabelText>
                                                </React.Fragment>
                                            ))
                                        }
                                    </Box>
                                ) : (
                                    AttributeComponent ? (
                                        <AttributeComponent
                                            id={id}
                                            value={value}
                                            link={link}
                                            customLink={customLink}
                                            onClick={onClick}
                                            match={match}
                                            color={color}
                                        />
                                    ) : (
                                        <Attribute
                                            id={id}
                                            value={value}
                                            link={link}
                                            customLink={customLink}
                                            onClick={onClick}
                                            match={match}
                                            color={color}
                                        />
                                    )
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Grid>
        </Box>
    );
};
Attributes.defaultProps = {
    count: 1,
};

export default withRouter(Attributes);
